import {
    Alert,
    Anchor,
    Group,
    Loader,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import {NavLink} from 'react-router-dom';

import {useTestObservationsBySyntheticUserQuery} from '../../test-observations/hooks/useTestObservationsQuery';
import {TriageSummary} from '../../test-observations/components/TriageSummary';
import {summarizeObservations} from '../../test-observations/utils/buildCascades';
import {toCascadeRows} from '../../test-observations/utils/cascadeRows';
import {ObservationsMiniTable} from './ObservationsMiniTable';

// Links a synthetic user to what it actually did. Backed by
// listTestObservationsBySyntheticUser (the synthetic-user-observed-index GSI) —
// never a client-side filter over the whole observation list.
//
// Reuses the observations module's classification, cascade grouping and summary
// wholesale, so a cascade reads identically here and on the Observations page.
export const ObservationsPanel = ({syntheticUserId}: { syntheticUserId: string }) => {

    const {
        data,
        isLoading,
        isError,
    } = useTestObservationsBySyntheticUserQuery(syntheticUserId);

    const records = data?.items ?? [];
    const rows = toCascadeRows(records);
    const summary = summarizeObservations(records);

    return (
        <Stack gap={'md'}>
            <Group justify={'space-between'} align={'center'}>
                <Title order={5}>Observations</Title>
                <Anchor
                    component={NavLink}
                    to={`/test-observations?syntheticUserId=${encodeURIComponent(syntheticUserId)}`}
                    size={'sm'}
                >
                    View all
                </Anchor>
            </Group>

            {isLoading && <Group><Loader size={'sm'}/><Text size={'sm'} c={'dimmed'}>Loading observations…</Text></Group>}

            {isError && (
                <Alert color='red' title='Could not load observations'>
                    The testing API did not return observations for this synthetic user.
                </Alert>
            )}

            {!isLoading && !isError && records.length === 0 && (
                <Text size={'sm'} c={'dimmed'}>
                    This synthetic user has not recorded any observations yet.
                </Text>
            )}

            {!isLoading && !isError && records.length > 0 && (
                <>
                    <TriageSummary summary={summary}/>
                    <ObservationsMiniTable rows={rows}/>
                </>
            )}
        </Stack>
    );
};
